import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Determine which auth token to use based on request URL or current path
function getAuthToken(config) {
  const url = config?.url || '';
  const path = window.location.pathname;
  if (url.includes('/admin') || path.startsWith('/admin')) {
    return localStorage.getItem('admin_auth_token');
  }
  return localStorage.getItem('hv_auth_token');
}

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Allow explicit token override via config header
  if (!config.headers.Authorization) {
    const token = getAuthToken(config);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      if (path.startsWith('/admin')) {
        localStorage.removeItem('admin_auth_token');
        window.location.href = '/admin/login';
      } else if (path.startsWith('/app')) {
        localStorage.removeItem('hv_auth_token');
        window.location.href = '/app/login';
      }
      localStorage.removeItem('user_data');
    }
    return Promise.reject(error);
  }
);

export default api;

// -----------------------------------------
// Public API
// -----------------------------------------

// Zugriffstoken der laufenden Bewertung (Besitzprüfung A1). Kommt beim Start vom Server
// und wird bei jeder weiteren Funnel-Anfrage mitgeschickt. Nur im Speicher dieses Tabs.
let reviewToken = null;
const rt = () => ({ headers: { 'X-Review-Token': reviewToken || '' } });

export const publicApi = {
  pingLive: (tenantSlug, linkSlug, visitorId) =>
    api.post(`/api/public/ping/${tenantSlug}/${linkSlug}`, {}, {
      headers: { 'x-visitor-id': visitorId }
    }),

  getTenantLinkInfo: (tenantSlug, linkSlug) =>
    api.get(`/api/public/tenant/${tenantSlug}/${linkSlug}`),
  
  startReview: (data) =>
    api.post('/api/public/reviews/start', data).then((res) => {
      reviewToken = res.data?.review_token || null;
      return res;
    }),
  
  updateCategories: (reviewId, data) =>
    api.patch(`/api/public/reviews/${reviewId}/categories`, data, rt()),
  
  analyzeRisk: (reviewId) =>
    api.post(`/api/public/reviews/${reviewId}/ai/analyze`, {}, rt()),
  
  rewriteText: (reviewId) =>
    api.post(`/api/public/reviews/${reviewId}/ai/rewrite`, {}, rt()),
  
  regenerateSuggestion: (reviewId) =>
    api.post(`/api/public/reviews/${reviewId}/ai/regenerate`, {}, rt()),

  refineSuggestion: (reviewId, instruction, currentText) =>
    api.post(`/api/public/reviews/${reviewId}/ai/refine`, {
      instruction,
      current_text: currentText,
    }, rt()),

  generateTitle: (reviewId, currentText) =>
    api.post(`/api/public/reviews/${reviewId}/ai/title`, { current_text: currentText }, rt()),
  
  chooseExternal: (reviewId, platform) =>
    api.post(`/api/public/reviews/${reviewId}/external/choose`, { platform }, rt()),
  
  setReaction: (reviewId, choice) =>
    api.post(`/api/public/reviews/${reviewId}/reaction`, { choice }, rt()),
  
  finishReview: (reviewId, email, contactWish = false) =>
    api.post(`/api/public/reviews/${reviewId}/finish`, { email, contact_wish: contactWish }, rt()),

  trackEvent: (reviewId, event) =>
    api.post(`/api/public/reviews/${reviewId}/track`, { event }, rt()),

  updateText: (reviewId, freeText) =>
    api.patch(`/api/public/reviews/${reviewId}/text`, { free_text: freeText }, rt()),

  sendProcessFeedback: (reviewId, text) =>
    api.post(`/api/public/reviews/${reviewId}/process-feedback`, { text }, rt()),

  abandonReview: (reviewId, step) =>
    api.post(`/api/public/reviews/${reviewId}/abandon`, { step }, rt()),
};

// -----------------------------------------
// Review Edit/Verify API
// -----------------------------------------

export const reviewEditApi = {
  consumeToken: (token) =>
    api.get(`/api/public/reviews/edit/consume?token=${token}`),
  
  updateReview: (reviewId, data, editToken) =>
    api.patch(`/api/public/reviews/edit/${reviewId}`, data, {
      headers: { Authorization: `Bearer ${editToken}` }
    }),
  
  verifyReview: (reviewId, editToken) =>
    api.post(`/api/public/reviews/edit/${reviewId}/verify`, {}, {
      headers: { Authorization: `Bearer ${editToken}` }
    }),

  deleteReview: (reviewId, editToken) =>
    api.delete(`/api/public/reviews/edit/${reviewId}`, {
      headers: { Authorization: `Bearer ${editToken}` }
    }),
};

// -----------------------------------------
// Auth API
// -----------------------------------------

export const authApi = {
  requestHVMagicLink: (email, privacyAccepted = false) =>
    api.post('/api/auth/hv/request-magic-link', { email, privacy_accepted: privacyAccepted }),
  
  consumeHVMagicLink: (token) =>
    api.get(`/api/auth/hv/consume?token=${token}`),
  
  requestAdminMagicLink: (email) =>
    api.post('/api/auth/admin/request-magic-link', { email }),
  
  consumeAdminMagicLink: (token) =>
    api.get(`/api/auth/admin/consume?token=${token}`),
  
  getCurrentUser: (token) =>
    token
      ? api.get('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      : api.get('/api/auth/me'),

  /** DEV only — instant JWT, no rate limit, no magic-link roundtrip */
  devQuickLogin: (role = 'hv', email = null) =>
    api.post('/api/auth/dev/quick-login', { role, ...(email ? { email } : {}) }),
};

// -----------------------------------------
// HV API
// -----------------------------------------

export const hvApi = {
  getLinks: () =>
    api.get('/api/hv/links'),

  getLiveSessions: () =>
    api.get('/api/hv/live'),

  createLink: (data) =>
    api.post('/api/hv/links', data),

  updateLink: (linkId, data) =>
    api.patch(`/api/hv/links/${linkId}`, data),
  
  getLinkReviews: (linkId, params) =>
    api.get(`/api/hv/links/${linkId}/reviews`, { params }),
  
  getStats: () =>
    api.get('/api/hv/stats'),

  updateTenant: (data) =>
    api.patch('/api/hv/tenant', data),

  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/api/hv/tenant/logo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  exportReviews: (linkId) =>
    api.get(`/api/hv/links/${linkId}/reviews/export`, { responseType: 'blob' }),

  getFunnelStats: (linkId) =>
    api.get(`/api/hv/links/${linkId}/funnel-stats`),

  getAllReviews: (params) =>
    api.get('/api/hv/reviews', { params }),

  markReviewAnswered: (reviewId) =>
    api.patch(`/api/hv/reviews/${reviewId}/answer`),

  markReviewUnread: (reviewId) =>
    api.patch(`/api/hv/reviews/${reviewId}/unread`),

  deleteReview: (reviewId) =>
    api.delete(`/api/hv/reviews/${reviewId}`),

  setReviewStatus: (reviewId, hv_status) =>
    api.patch(`/api/hv/reviews/${reviewId}/status`, { hv_status }),

  setDefaultLink: (linkId) =>
    api.patch(`/api/hv/links/${linkId}/set-default`),

  getChartData: (range = '1y') =>
    api.get('/api/hv/chart', { params: { range } }),

  getAggregateFunnelStats: () =>
    api.get('/api/hv/funnel-stats'),

  // Klickfluss inkl. Verfeinerungs-Schritt und Wandlung kritisch↔positiv
  getFunnelFlow: (linkId) =>
    api.get('/api/hv/funnel-flow', { params: linkId ? { link_id: linkId } : {} }),

  createCheckoutSession: (plan) =>
    api.post('/api/hv/billing/create-checkout-session', { plan }),

  getBillingStatus: () =>
    api.get('/api/hv/billing/status'),

  createPortalSession: () =>
    api.post('/api/hv/billing/portal-session'),

  getStripePlans: () =>
    api.get('/api/hv/billing/plans'),

  completeOnboarding: (data) =>
    api.post('/api/hv/onboarding', data),

  redeemCoupon: (code) =>
    api.post('/api/hv/redeem-coupon', { code }),

  // Betroffenenrechte (Art. 15/17/20 DSGVO)
  exportAccount: () =>
    api.get('/api/hv/me/export', { responseType: 'blob' }),

  deleteAccount: (confirm) =>
    api.delete('/api/hv/me', { data: { confirm } }),

  backfillSentiment: (limit = 200) =>
    api.post('/api/hv/reviews/backfill-sentiment', null, { params: { limit } }),
};

// -----------------------------------------
// Admin API
// -----------------------------------------

export const adminApi = {
  getTenants: () =>
    api.get('/api/admin/tenants'),
  
  getTenant: (tenantId) =>
    api.get(`/api/admin/tenants/${tenantId}`),
  
  updateTenant: (tenantId, data) =>
    api.patch(`/api/admin/tenants/${tenantId}`, data),
  
  createTenant: (data) =>
    api.post('/api/admin/tenants', data),
  
  resetTenantLogin: (tenantId) =>
    api.post(`/api/admin/tenants/${tenantId}/reset-login`),
  
  createHVUser: (tenantId, data) =>
    api.post(`/api/admin/tenants/${tenantId}/hv-user`, data),
  
  getReviews: (params) =>
    api.get('/api/admin/reviews', { params }),
  
  getPrompts: () =>
    api.get('/api/admin/prompts'),
  
  updatePrompt: (key, data) =>
    api.patch(`/api/admin/prompts/${key}`, data),
  
  getMetaScores: (params) =>
    api.get('/api/admin/meta-scores', { params }),
  
  createMetaScore: (tenantId, data) =>
    api.post(`/api/admin/meta-scores?tenant_id=${tenantId}`, data),

  // Gutscheine
  getCoupons: () =>
    api.get('/api/admin/coupons'),

  createCoupon: (data) =>
    api.post('/api/admin/coupons', data),

  updateCoupon: (couponId, data) =>
    api.patch(`/api/admin/coupons/${couponId}`, data),

  deleteCoupon: (couponId) =>
    api.delete(`/api/admin/coupons/${couponId}`),

  deleteTenant: (tenantId) =>
    api.delete(`/api/admin/tenants/${tenantId}`),

  retentionPreview: () =>
    api.get('/api/admin/retention/preview'),
};
